/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

function DeliveryProfilesDeliveryEmailsEditViewController(
    this: JS5Object,
    _: JS5Object,
    $location: JS5Object,
    $timeout: JS5Function,
    $q: JS5Object,
    $routeParams:JS5Object,
    BreadcrumbService: JS5Object,
    ModalService: JS5Object,
    NotificationService: JS5Object,
    PartnerEditService: JS5Object,
    PartnerResourceFactory: JS5Object
) {

    const setupBreadcrumbs = () => {
        BreadcrumbService.clearBreadcrumbs();
        BreadcrumbService.addBreadcrumb({
            name: 'Partners',
            "location": "/system/partners/search",
            "params": {
                "tab": "properties"
            },
        });
        BreadcrumbService.addBreadcrumb({
            name: this.partnerName,
            location: `/system/partners/edit/${$routeParams.id}/deliveryProfiles`
        });
        BreadcrumbService.addBreadcrumb({
            name: 'Delivery Profiles',
            location: `/system/partners/edit/${$routeParams.id}/deliveryProfiles/edit/${$routeParams.deliveryProfileId}`,
            params: {tab: 'emails'}
        });
    };

    const navigateToDeliveryEmailSearchView = () => {
        $location.url(`/system/partners/edit/${$routeParams.id}/deliveryProfiles/edit/${$routeParams.deliveryProfileId}?tab=emails`);
    };

    const navigateToNewDeliveryEmailEditView = (newDeliveryEmail: JS5Object): void => {
        $location.url(`/system/partners/edit/${$routeParams.id}/deliveryProfiles/edit/${$routeParams.deliveryProfileId}/emails/edit/${newDeliveryEmail.id}`);
    };

    //=================//
    // Data Management //
    //=================//

    let originalDeliveryEmail: JS5Object;

    const getOriginalDeliveryEmail = () => {
        if ($routeParams.deliveryEmailId === '-1') {
            return $q.when(null);
        }
        return PartnerEditService.getDeliveryEmail($routeParams.deliveryProfileId, $routeParams.deliveryEmailId);
    };

    const getEditData = () => {
        this.loadingIndicator.show = true;
        return $q.all([
            PartnerResourceFactory.get({id: $routeParams.id}).$promise
                .then((partner: JS5Object) => {
                    this.partnerName = partner.name;
                }),
            PartnerEditService.getDeliveryProfile($routeParams.deliveryProfileId)
                .then((deliveryProfile: JS5Object) => {
                    this.deliveryProfile = deliveryProfile;
                }),
            getOriginalDeliveryEmail()
                .then((deliveryEmail: JS5Object) => {
                    originalDeliveryEmail = deliveryEmail;
                    if (deliveryEmail === null) {
                        this.deliveryEmail = {
                            attachments: []
                        };
                    } else {
                        this.deliveryEmail = _.cloneDeep(deliveryEmail);
                    }
                    this.gridOptions.data = this.deliveryEmail.attachments;
                    this.editorForm.$setPristine();
                }),
            PartnerEditService.getEmailEndpointsForPartner($routeParams.id)
                .then((availableEndpoints: JS5Object) => {
                    this.availableEndpoints = availableEndpoints;
                }),
            PartnerEditService.getDeliveryItemsForProfile({
                // From results of flexClient query, looks like it isn't filtered on profileId
                profileId: $routeParams.deliveryProfileId,
                includeHidden: false,
            }).then((deliveryItems: JS5Object[]) => {
                this.availableDeliveryItems = _.uniqBy(deliveryItems, 'sourceMediaAcceptProfile.id');
                _.remove(this.availableDeliveryItems, (deliveryItem: JS5Object) => {
                    return !_.has(deliveryItem, 'sourceMediaAcceptProfile.id');
                });
            })
        ]).then(() => {
            this.loadingIndicator.show = false;
        });
    };


    //======//
    // Grid //
    //======//

    const colDefs: JS5Object[] = [
        {
            cellTemplate: 'components/visual/grid/check-mark-template.html',
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            field: 'checkMark',
            fieldName: 'Selection',
            headerCellTemplate: 'components/grids/default-artwork-maps-grid/cell-templates/header-check-mark-row-count-template.html',
            width: 55,
        },
        {
            cellTemplate: 'components/visual/grid/base-tooltip-template.html',
            enableColumnMenu: false,
            enableSorting: false,
            field: 'deliveryItem.sourceMediaAcceptProfile.name',
            displayName: 'Media Accept Profile',
            width: 455,
        },
        {
            cellTemplate: 'system/templates/mediaAcceptProfiles/searchView/renderers/accept-profile-tooltip-template.html',
            enableColumnMenu: false,
            enableSorting: false,
            field: 'deliveryItem.sourceMediaAcceptProfile.type',
            displayName: 'Type',
            width: 150,
        }
    ];

    this.gridOptions = {
        selectionRowHeaderColName: 'Selection',
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: true,
        enableColumnResizing: false,
        enablePaginationControls: false,
        enableFullRowSelection: true,
        enableSorting: true,
        rowHeight: 26,
        multiSelect: true,
        minRowsToShow: 5,
        columnDefs: colDefs,
        data: [],
        onRegisterApi: (gridApi: JS5Object) => {
            this.gridApi = gridApi;
        }
    };

    this.areAllRowsSelected = () => {
        return this.gridApi.selection.getSelectedCount() === this.gridOptions.data.length;
    };

    this.onSelectAllToggled = () => {
        if (this.areAllRowsSelected()) {
            this.gridApi.selection.clearSelectedRows();
        } else {
            this.gridApi.selection.selectAllRows();
        }
    };


    //==================//
    // HTML Interfacing //
    //==================//

    this.onAddAttachmentClicked = () => {
        let currentDeliveryItems = _.map(this.gridOptions.data, 'deliveryItem');
        let remainingOptions = _.difference(this.availableDeliveryItems, currentDeliveryItems, 'id');
        ModalService.showAttachmentSelectModal(remainingOptions, this.onAttachmentsSelected);
    };

    this.onAttachmentsSelected = (selectedDeliveryItems: JS5Object[]) => {
        let newAttachments = _.chain(selectedDeliveryItems)
            .map((deliveryItem: JS5Object) => {
                return {
                    deliveryItem: deliveryItem
                };
            })
            .differenceBy(this.gridOptions.data, 'deliveryItem.sourceMediaAcceptProfile.id')
            .value();

        this.gridOptions.data.splice(this.gridOptions.data.length, 0, ...newAttachments);
        this.editorForm.$setDirty();
    };

    this.onRemoveAttachmentsClicked = () => {
        let selectedRows = this.gridApi.selection.getSelectedRows();
        if (selectedRows.length > 0) {
            _.pullAll(this.gridOptions.data, selectedRows);
            this.gridApi.selection.clearSelectedRows();
        }
        this.editorForm.$setDirty();
    };

    const canSave = () => {
        return this.editorForm.$dirty && this.editorForm.$valid;
    };

    const onSaveClicked = () => {
        this.loadingIndicator.show = true;

        if (originalDeliveryEmail) {
            PartnerEditService.updateDeliveryEmail(this.deliveryProfile.id, this.deliveryEmail)
                .then(getEditData);

        } else {
            PartnerEditService.createDeliveryEmail(this.deliveryProfile.id, this.deliveryEmail)
                .then((newDeliveryEmail: JS5Object) => {
                    navigateToNewDeliveryEmailEditView(newDeliveryEmail);
                });
        }
    };

    const onRevertClicked = () => {
        if (originalDeliveryEmail) {
            this.deliveryEmail = _.cloneDeep(originalDeliveryEmail);
            this.gridOptions.data = this.deliveryEmail.attachments;
            this.editorForm.$setPristine();
        } else {
            navigateToDeliveryEmailSearchView();
        }
    };

    const onCloseClicked = () => {
        if (this.editorForm.$pristine) {
            navigateToDeliveryEmailSearchView();

        } else {
            NotificationService.createWarningNotification(
                'Please save or revert your changes before navigating away from this page.'
            );
        }
    };

    this.toolbarButtonDefs = [
        {
            label: 'Save',
            enabled: canSave,
            onClick: onSaveClicked
        },
        {
            label: 'Revert',
            enabled: () => originalDeliveryEmail === null || this.editorForm.$dirty,
            onClick: onRevertClicked
        },
        {
            label: 'Close',
            enabled: () => true,
            onClick: onCloseClicked
        }
    ];


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = () => {
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };

        getEditData().then(setupBreadcrumbs);
    };
}


angular.module('prism-ui.deliveryProfiles').component('deliveryEmailEditView', {
    templateUrl: 'system/partner/editor/components/view/deliveryProfiles/editor/components/emails/editor/delivery-profiles-delivery-emails-edit-view-template.html',
    controller: DeliveryProfilesDeliveryEmailsEditViewController
});
